import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Handshake, PaperPlaneRight, Storefront, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, mediaUrl } from "../lib/api";

function Avatar({ url, name }) {
  return url ? (
    <img src={mediaUrl(url)} alt="" className="h-9 w-9 rounded-full border border-white/15 object-cover" />
  ) : (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-neon-violet/40 bg-neon-violet/10 font-display text-xs font-black text-neon-violet">
      {(name || "?")[0].toUpperCase()}
    </div>
  );
}

export default function MessagesPanel({ userRole }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [contacts, setContacts] = useState([]);
  const [sending, setSending] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offer, setOffer] = useState({ rate: "", night: "", cutoff: "", note: "" });
  const bottomRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = active?.id;

  const loadThreads = () => API.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => {});
  const loadThread = (id) => API.get(`/messages/thread/${id}`).then((r) => setMessages(r.data.messages)).catch(() => {});

  useEffect(() => {
    loadThreads();
    if (userRole === "promoter") {
      API.get("/venues").then((r) => setContacts(r.data.map((v) => ({ id: v.owner_user_id, label: v.name, sub: v.address })))).catch(() => {});
    } else {
      API.get("/promoters").then((r) => setContacts(r.data.map((p) => ({ id: p.id, label: p.name, sub: p.handle })))).catch(() => {});
    }
    const timer = setInterval(() => {
      loadThreads();
      if (activeRef.current) loadThread(activeRef.current);
    }, 8000);
    return () => clearInterval(timer);
  }, [userRole]);

  useEffect(() => {
    if (active) { loadThread(active.id); setMessages([]); }
  }, [active?.id]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openThread = (t) => setActive({ id: t.other.id, name: t.venue_name || t.other.name, handle: t.other.handle, avatar_url: t.other.avatar_url });

  const startNew = (id) => {
    const c = contacts.find((x) => x.id === id);
    if (c) setActive({ id: c.id, name: c.label, handle: c.sub, avatar_url: null });
  };

  const send = async () => {
    if (!draft.trim() || !active) return;
    setSending(true);
    try {
      await API.post("/messages", { to_id: active.id, body: draft.trim() });
      setDraft("");
      await loadThread(active.id);
      loadThreads();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSending(false); }
  };

  const sendOffer = async () => {
    if (!offer.rate || !offer.night.trim()) { toast.error("Rate and night are required"); return; }
    setSending(true);
    try {
      await API.post("/messages/offer", {
        to_id: active.id, rate: Number(offer.rate), night: offer.night.trim(),
        cutoff: offer.cutoff.trim(), note: offer.note.trim(),
      });
      setOffer({ rate: "", night: "", cutoff: "", note: "" });
      setOfferOpen(false);
      await loadThread(active.id);
      loadThreads();
      toast.success("Offer sent — they'll get a notification");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSending(false); }
  };

  const respondOffer = async (messageId, action) => {
    try {
      const { data } = await API.post(`/messages/${messageId}/respond`, { action });
      toast.success(`Offer ${data.status}`);
      await loadThread(active.id);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (active) {
    return (
      <div className="rounded-3xl border border-white/10 bg-[#0a0a0a]" data-testid="message-thread">
        <div className="flex items-center gap-3 border-b border-white/10 p-4">
          <button data-testid="thread-back-btn" onClick={() => { setActive(null); loadThreads(); }}
            className="glass rounded-full p-2"><ArrowLeft size={14} /></button>
          <Avatar url={active.avatar_url} name={active.name} />
          <div className="min-w-0">
            <div className="truncate text-sm font-black" data-testid="thread-title">{active.name}</div>
            <div className="truncate text-[11px] text-neon-violet">{active.handle}</div>
          </div>
        </div>
        <div className="max-h-[50vh] space-y-2 overflow-y-auto p-4" data-testid="thread-messages">
          {messages.map((m) => {
            if (m.kind === "offer") {
              const o = m.offer;
              const mine = m.from_id !== active.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className="max-w-[85%] rounded-2xl border border-neon-gold/40 bg-neon-gold/5 p-4" data-testid={`offer-card-${m.id}`}>
                    <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-neon-gold">
                      <Handshake size={14} weight="fill" /> Booking Offer — {o.venue_name}
                    </div>
                    <div className="mt-2 font-display text-xl font-black">${o.rate}<span className="text-sm text-zinc-400">/guest</span></div>
                    <div className="mt-1 text-xs text-zinc-300">{o.night}{o.cutoff ? ` · cutoff ${o.cutoff}` : ""}</div>
                    {o.note && <div className="mt-1 text-[11px] text-zinc-500">{o.note}</div>}
                    {o.status === "pending" && !mine ? (
                      <div className="mt-3 flex gap-2">
                        <button data-testid={`offer-accept-${m.id}`} onClick={() => respondOffer(m.id, "accept")}
                          className="rounded-full bg-neon-green px-4 py-1.5 text-[11px] font-black text-black">Accept</button>
                        <button data-testid={`offer-decline-${m.id}`} onClick={() => respondOffer(m.id, "decline")}
                          className="rounded-full border border-white/20 px-4 py-1.5 text-[11px] font-bold text-zinc-300">Decline</button>
                      </div>
                    ) : (
                      <div className={`mt-3 inline-block rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-wider ${o.status === "accepted" ? "bg-neon-green/15 text-neon-green" : o.status === "declined" ? "bg-red-500/15 text-red-400" : "bg-neon-gold/15 text-neon-gold"}`}
                        data-testid={`offer-status-${m.id}`}>
                        {o.status}
                      </div>
                    )}
                  </div>
                </div>
              );
            }
            return (
              <div key={m.id} className={`flex ${m.from_id !== active.id ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${m.from_id !== active.id ? "bg-neon-gold text-black" : "border border-white/10 bg-white/5"}`}>
                  {m.body}
                </div>
              </div>
            );
          })}
          {messages.length === 0 && (
            <p className="py-8 text-center text-xs text-zinc-600">Start the conversation — book a night, split a promo, plan a drop.</p>
          )}
          <div ref={bottomRef} />
        </div>
        <div className="border-t border-white/10 p-4">
          {userRole === "venue" && (
            <div className="mb-3">
              <button data-testid="offer-toggle-btn" onClick={() => setOfferOpen((o) => !o)}
                className="flex items-center gap-1.5 rounded-full border border-neon-gold/40 px-3.5 py-1.5 text-[11px] font-bold text-neon-gold">
                <Handshake size={13} weight="fill" /> {offerOpen ? "Cancel Offer" : "Send Booking Offer"}
              </button>
              {offerOpen && (
                <div className="mt-3 grid grid-cols-2 gap-2 rounded-2xl border border-neon-gold/20 bg-black/40 p-3" data-testid="offer-form">
                  <input data-testid="offer-rate" type="number" min="0.5" step="0.5" value={offer.rate}
                    onChange={(e) => setOffer({ ...offer, rate: e.target.value })} placeholder="$ per guest"
                    className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm outline-none focus:border-neon-gold" />
                  <input data-testid="offer-night" value={offer.night}
                    onChange={(e) => setOffer({ ...offer, night: e.target.value })} placeholder="Night (e.g. Friday)"
                    className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm outline-none focus:border-neon-gold" />
                  <input data-testid="offer-cutoff" value={offer.cutoff}
                    onChange={(e) => setOffer({ ...offer, cutoff: e.target.value })} placeholder="Cutoff (e.g. 10 PM)"
                    className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm outline-none focus:border-neon-gold" />
                  <input data-testid="offer-note" value={offer.note}
                    onChange={(e) => setOffer({ ...offer, note: e.target.value })} placeholder="Note (optional)" maxLength={300}
                    className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm outline-none focus:border-neon-gold" />
                  <button data-testid="offer-send-btn" onClick={sendOffer} disabled={sending}
                    className="col-span-2 rounded-full bg-neon-gold py-2 text-xs font-black text-black shadow-glow-gold disabled:opacity-40">
                    Send Offer
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input data-testid="message-input" value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type a message…" maxLength={1000}
              className="flex-1 rounded-full border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-gold" />
            <button data-testid="message-send-btn" onClick={send} disabled={sending || !draft.trim()}
              className="rounded-full bg-neon-gold p-2.5 text-black shadow-glow-gold disabled:opacity-40">
              <PaperPlaneRight size={18} weight="fill" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-4" data-testid="messages-panel">
      <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
          {userRole === "promoter" ? "Message a venue" : "Message a promoter"}
        </div>
        <select data-testid="new-message-select" defaultValue="" onChange={(e) => e.target.value && startNew(e.target.value)}
          className="mt-2 w-full rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-violet">
          <option value="" disabled>
            {userRole === "promoter" ? "Pick a venue…" : "Pick a promoter…"}
          </option>
          {contacts.map((c) => <option key={c.id} value={c.id}>{c.label}{c.sub ? ` · ${c.sub}` : ""}</option>)}
        </select>
      </div>

      <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-5">
        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Conversations</div>
        {threads.length === 0 ? (
          <p className="py-4 text-sm text-zinc-600">No conversations yet.</p>
        ) : threads.map((t) => (
          <button key={t.other.id} data-testid={`thread-${t.other.id}`} onClick={() => openThread(t)}
            className="flex w-full items-center gap-3 rounded-2xl px-2 py-2.5 text-left transition-colors hover:bg-white/5">
            <Avatar url={t.other.avatar_url} name={t.venue_name || t.other.name} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-bold">{t.venue_name || t.other.name}</span>
                {t.other.role === "venue"
                  ? <Storefront size={12} className="shrink-0 text-neon-gold" />
                  : <UsersThree size={12} className="shrink-0 text-neon-violet" />}
                {t.unread > 0 && (
                  <span className="rounded-full bg-neon-gold px-1.5 py-0.5 text-[9px] font-black text-black" data-testid={`thread-unread-${t.other.id}`}>{t.unread}</span>
                )}
              </div>
              <div className="truncate text-[11px] text-zinc-500">{t.last.mine ? "You: " : ""}{t.last.body}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
